import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, GraduationCap, HeartHandshake, LineChart, Target } from 'lucide-react';
import { Footer } from '../components/layout/Footer';
import { SpecialThanksBox } from '../components/ui/SpecialThanksBox';

const secondaryActions = [
  {
    to: '/learn-stocks-daily',
    label: 'Stock of the Day',
    sublabel: 'Daily market lesson',
    Icon: LineChart,
    iconClassName: 'bg-foreground/[0.08] text-foreground dark:bg-white/10 dark:text-white',
  },
  {
    to: '/course',
    label: 'Foundations Course',
    sublabel: 'Learn the basics',
    Icon: GraduationCap,
    iconClassName: 'bg-emerald-600/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
  },
  {
    to: '/membership',
    label: 'Become a Member',
    sublabel: 'Support Wallstreetle',
    Icon: HeartHandshake,
    iconClassName: 'bg-amber-500/[0.12] text-amber-700 dark:bg-amber-300/10 dark:text-amber-300',
  },
];

const HomePage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | Learn Stocks by Playing Daily Games</title>
        <meta
          name="description"
          content="Learn about stocks through daily games built on real stock market data. Play Classic or Trivia Rush."
        />
      </Helmet>

      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <svg
          className="absolute top-[15%] -left-[5%] w-1/2 opacity-[0.04]"
          viewBox="0 0 600 200"
          fill="none"
        >
          <path
            d="M0 160 Q50 155 100 140 T200 110 T300 80 T400 50 T500 30 T600 10"
            stroke="#2DB87A"
            strokeWidth="3"
          />
        </svg>
        <svg
          className="absolute bottom-[10%] -right-[5%] w-[45%] opacity-[0.04] -scale-x-100"
          viewBox="0 0 500 180"
          fill="none"
        >
          <path
            d="M0 150 Q60 140 120 120 T240 90 T360 45 T500 15"
            stroke="#E8A838"
            strokeWidth="3"
          />
        </svg>
      </div>

      {/* Hero */}
      <section className="relative z-[1] flex flex-col items-center text-center pt-10 pb-6 sm:pt-16 sm:pb-10 px-6 animate-fade-up opacity-0">
        <h2 className="font-serif text-[clamp(1.8rem,6vw,4.5rem)] leading-[1.08] tracking-tight text-foreground max-w-[700px] mb-5">
          Welcome to Wallstreetle!
        </h2>
        <p className="text-[clamp(1rem,2vw,1.2rem)] text-muted-foreground max-w-[660px] leading-relaxed">
          Learn about stocks through daily games built on real stock market data
        </p>
      </section>

      {/* Game Cards */}
      <section className="relative z-[1] flex flex-col gap-2.5 md:gap-4 px-4 md:px-6 pb-10 md:pb-20 max-w-[1000px] mx-auto animate-fade-up-delay opacity-0">
        {/* Classic Card — Hero */}
        <Link
          to="/popular"
          className="group relative w-full bg-white/50 dark:bg-white/5 backdrop-blur-2xl border-[1.5px] border-white/60 dark:border-white/10 rounded-[20px] px-5 md:px-8 py-4 md:pt-10 md:pb-8 shadow-[0_8px_32px_rgba(60,50,30,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_16px_48px_rgba(60,50,30,0.14)] dark:hover:shadow-[0_16px_48px_rgba(0,0,0,0.3)] hover:border-white/80 dark:hover:border-white/20 overflow-hidden text-center no-underline cursor-pointer"
        >
          {/* Top accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-foreground rounded-t-[20px] group-hover:h-[5px] transition-all" />

          <div className="flex items-center justify-center gap-3.5 mb-1 md:mb-4">
            <span className="font-serif text-[1.35rem] md:text-[2.2rem] tracking-tight text-foreground">
              Guess the Stock
            </span>
          </div>
          <img
            src="/assets/share-link-image.png"
            alt="Wallstreetle — Can You Guess the Stock?"
            className="mx-auto my-2 md:my-4 w-[70%] max-w-[420px] rounded-xl shadow-sm"
          />
          <span className="block text-[0.7rem] md:text-[0.75rem] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Intermediate
          </span>
          <p className="hidden md:block text-[1.05rem] font-semibold text-foreground mb-3">
            Like Wordle but for Stocks
          </p>
          <span className="inline-flex items-center gap-2 mt-2 md:mt-6 font-semibold text-[0.85rem] md:text-[0.9rem] text-foreground group-hover:gap-3 transition-all">
            Play Classic
            <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-0.5 transition-transform" />
          </span>
        </Link>

        {/* Secondary Cards Row */}
        <div className="flex flex-col md:flex-row gap-2.5 md:gap-5">
          {/* Compare the Pair Card */}
          <Link
            to="/compare-the-pair"
            className="group relative flex-1 bg-white/50 dark:bg-white/5 backdrop-blur-2xl border-[1.5px] border-white/60 dark:border-white/10 rounded-[20px] px-5 md:px-9 py-4 md:pt-10 md:pb-9 shadow-[0_8px_32px_rgba(60,50,30,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_16px_48px_rgba(60,50,30,0.14)] dark:hover:shadow-[0_16px_48px_rgba(0,0,0,0.3)] hover:border-white/80 dark:hover:border-white/20 overflow-hidden text-center no-underline cursor-pointer"
          >
            {/* Top accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-foreground rounded-t-[20px] group-hover:h-[5px] transition-all" />

            <span className="absolute top-3 right-3 bg-foreground text-background font-sans text-[0.65rem] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-md animate-scale-in z-10">
              NEW
            </span>

            <div className="flex items-center justify-center mb-1 md:mb-4">
              <span className="font-serif text-[1.35rem] md:text-[1.65rem] tracking-tight text-foreground">
                Compare the Pair
              </span>
            </div>
            <span className="block text-[0.7rem] md:text-[0.75rem] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Beginner-Friendly
            </span>
            <p className="hidden md:block text-[1.05rem] font-semibold text-foreground mb-3">
              Two stocks. Ten questions. One winner.
            </p>
            <span className="inline-flex items-center gap-2 mt-2 md:mt-6 font-semibold text-[0.85rem] md:text-[0.9rem] text-foreground group-hover:gap-3 transition-all">
              Play Compare
              <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-0.5 transition-transform" />
            </span>

            {/* Decorative VS */}
            <div className="absolute bottom-3 right-5 font-serif text-[4rem] leading-none opacity-[0.04] pointer-events-none text-foreground">
              VS
            </div>
          </Link>

          {/* Trivia Rush Card */}
          <Link
            to="/trivia-rush"
            className="group relative flex-1 bg-white/50 dark:bg-white/5 backdrop-blur-2xl border-[1.5px] border-white/60 dark:border-white/10 rounded-[20px] px-5 md:px-9 py-4 md:pt-10 md:pb-9 shadow-[0_8px_32px_rgba(60,50,30,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_16px_48px_rgba(60,50,30,0.14)] dark:hover:shadow-[0_16px_48px_rgba(0,0,0,0.3)] hover:border-white/80 dark:hover:border-white/20 overflow-hidden text-center no-underline cursor-pointer"
          >
            {/* Top accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-foreground rounded-t-[20px] group-hover:h-[5px] transition-all" />

            <span className="absolute top-3 right-3 bg-foreground text-background font-sans text-[0.65rem] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-md animate-scale-in z-10">
              NEW
            </span>

            <div className="flex items-center justify-center mb-1 md:mb-4">
              <span className="font-serif text-[1.35rem] md:text-[1.65rem] tracking-tight text-foreground">
                Trivia Rush
              </span>
            </div>
            <span className="block text-[0.7rem] md:text-[0.75rem] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Expert
            </span>
            <p className="hidden md:block text-[1.05rem] font-semibold text-foreground mb-3">
              Higher or Lower? Don't Break the Streak.
            </p>
            <span className="inline-flex items-center gap-2 mt-2 md:mt-6 font-semibold text-[0.85rem] md:text-[0.9rem] text-foreground group-hover:gap-3 transition-all">
              Play Trivia Rush
              <ArrowRight className="w-[18px] h-[18px] group-hover:translate-x-0.5 transition-transform" />
            </span>

            {/* Decorative question mark */}
            <div className="absolute bottom-3 right-5 font-serif text-[5rem] leading-none opacity-[0.04] pointer-events-none text-foreground">
              ?
            </div>
          </Link>
        </div>
      </section>

      {/* Secondary Actions */}
      <section className="relative z-[1] flex flex-col gap-3 px-4 md:px-6 max-w-[1000px] mx-auto animate-fade-up-delay opacity-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 md:gap-3">
          {secondaryActions.map(({ to, label, sublabel, Icon, iconClassName }) => (
            <Link
              key={to}
              to={to}
              className="group flex min-h-[72px] items-center gap-3 rounded-xl border border-white/60 dark:border-white/10 bg-white/45 dark:bg-white/[0.04] px-4 py-3 text-left shadow-[0_6px_22px_rgba(60,50,30,0.055)] dark:shadow-[0_6px_22px_rgba(0,0,0,0.14)] backdrop-blur-2xl transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-white/80 dark:hover:border-white/20 hover:bg-white/60 dark:hover:bg-white/[0.06] hover:shadow-[0_10px_28px_rgba(60,50,30,0.075)] no-underline cursor-pointer"
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}>
                <Icon className="h-[19px] w-[19px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[0.92rem] font-semibold leading-tight text-foreground">
                  {label}
                </span>
                <span className="mt-1 block truncate text-[0.76rem] font-medium leading-tight text-muted-foreground">
                  {sublabel}
                </span>
              </span>
              <ArrowRight className="h-[17px] w-[17px] shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
            </Link>
          ))}
        </div>

        <div className="mt-3 overflow-hidden rounded-xl border border-white/60 bg-white/45 shadow-[0_6px_22px_rgba(60,50,30,0.055)] backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_6px_22px_rgba(0,0,0,0.14)]">
          <div className="h-px bg-gradient-to-r from-transparent via-foreground/[0.12] to-transparent dark:via-white/[0.14]" />
          <div className="grid gap-4 px-5 py-5 md:grid-cols-[170px_1fr] md:gap-6 md:px-6">
            <div className="flex flex-col items-center gap-2 text-center md:items-center">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-600/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300">
                <Target className="h-[18px] w-[18px]" />
              </span>
              <p className="text-[0.68rem] font-semibold uppercase tracking-wider text-muted-foreground">
                Our Mission
              </p>
            </div>
            <p className="max-w-[720px] text-[0.95rem] leading-relaxed text-muted-foreground md:text-[1.02rem]">
              Wallstreetle aims to improve financial literacy by turning stock market learning into a daily game.
              We help players build confidence with real company data, useful context, and approachable challenges that make investing concepts easier to understand over time.
            </p>
          </div>
        </div>
      </section>

      {/* Special Thanks */}
      <section className="relative z-[1] mt-4 mb-10 px-4 md:mt-5 md:mb-16 md:px-6 max-w-[1000px] mx-auto">
        <SpecialThanksBox variant="landing" />
      </section>

      {/* Footer rendered inline so it's only visible on scroll */}
      <Footer />
    </>
  );
};

export default HomePage;
